import { create } from 'zustand';

import type { OutgoingChatMessage } from './chatTransport';

/** Where a message the reader sent stands until its stored copy arrives. */
export type OutboxStatus = 'pending' | 'failed';

export type OutboxEntry = {
  conversationId: string;
  message: OutgoingChatMessage;
  /** When Send was pressed; the bubble shows here until the stored copy replaces it. */
  createdAt: Date;
  status: OutboxStatus;
};

type OutboxState = {
  /** Per conversation, oldest first. */
  entries: Record<string, OutboxEntry[]>;
  put: (entry: OutboxEntry) => void;
  setStatus: (conversationId: string, clientMessageId: string, status: OutboxStatus) => void;
  remove: (conversationId: string, clientMessageId: string) => void;
  /** Drops every entry whose stored copy has arrived. */
  settle: (conversationId: string, deliveredIds: ReadonlySet<string>) => void;
};

const NO_ENTRIES: OutboxEntry[] = [];

function replaceEntries(
  state: OutboxState,
  conversationId: string,
  next: OutboxEntry[]
): Pick<OutboxState, 'entries'> | OutboxState {
  const current = state.entries[conversationId] ?? NO_ENTRIES;

  if (next.length === current.length && next.every((entry, index) => entry === current[index])) {
    return state;
  }

  return { entries: { ...state.entries, [conversationId]: next } };
}

/**
 * Inquiry messages the reader sent that the conversation does not show yet.
 * Used only for a conversation whose sends are idempotent: the chat shows the
 * message at once and retries it under the same id, so a stored copy can
 * always replace its entry and never sit beside it. An entry leaves when its
 * stored copy arrives, matched by `clientMessageId`, whichever way it came.
 *
 * In memory only, as a support thread's pending messages are: a send the app
 * did not finish before it closed is one the reader has not been told failed.
 */
export const useOutboxStore = create<OutboxState>((set) => ({
  entries: {},
  put: (entry) =>
    set((state) => {
      const others = (state.entries[entry.conversationId] ?? NO_ENTRIES).filter(
        (candidate) => candidate.message.clientMessageId !== entry.message.clientMessageId
      );

      return replaceEntries(state, entry.conversationId, [...others, entry]);
    }),
  setStatus: (conversationId, clientMessageId, status) =>
    set((state) =>
      replaceEntries(
        state,
        conversationId,
        (state.entries[conversationId] ?? NO_ENTRIES).map((entry) =>
          entry.message.clientMessageId === clientMessageId && entry.status !== status
            ? { ...entry, status }
            : entry
        )
      )
    ),
  remove: (conversationId, clientMessageId) =>
    set((state) =>
      replaceEntries(
        state,
        conversationId,
        (state.entries[conversationId] ?? NO_ENTRIES).filter(
          (entry) => entry.message.clientMessageId !== clientMessageId
        )
      )
    ),
  settle: (conversationId, deliveredIds) =>
    set((state) =>
      replaceEntries(
        state,
        conversationId,
        (state.entries[conversationId] ?? NO_ENTRIES).filter(
          (entry) => !deliveredIds.has(entry.message.clientMessageId)
        )
      )
    ),
}));

export function selectOutbox(conversationId: string) {
  return (state: OutboxState): OutboxEntry[] => state.entries[conversationId] ?? NO_ENTRIES;
}
